import React, { useState, useEffect } from 'react';
import { translate as t } from 'hooks/useTranslations';
import { useManageSolutionsContext } from '../../../context';
import SolutionCard from '../../../components/SolutionCard';
import View from './components/View';
import Edit from './components/Edit';
import { useRiskPoolPolicyConfig, useUpdateRiskPoolPolicyConfig } from './_data_sources/useRiskPoolPolicyConfig';
import { RiskPoolPolicySolution } from '../../../_types/solution';
import { AppContext } from '../../../../../../context';
import errorHelper from 'helpers/errorHelper';
import { ServerErrorResponse } from 'data_sources/constants';

const Policy: React.FC = () => {
  const { selectedSolution } = useManageSolutionsContext();
  const { currentOrganizationId } = React.useContext(AppContext);

  const { data: configData, isLoading: isConfigLoading } = useRiskPoolPolicyConfig(currentOrganizationId);
  const { mutateAsync: updateConfig, isLoading: isUpdateLoading } = useUpdateRiskPoolPolicyConfig();

  const [localSolutions, setLocalSolutions] = useState<RiskPoolPolicySolution[]>([]);

  // Sync local state with fetched data
  useEffect(() => {
    if (configData?.riskPoolSolutions) {
      setLocalSolutions(configData.riskPoolSolutions);
    }
  }, [configData]);

  const handleSolutionsChange = (updatedSolutions: RiskPoolPolicySolution[]) => {
    setLocalSolutions(updatedSolutions);
  };

  // Handle save button click
  const handleSaveClick = async () => {
    try {
      await updateConfig({
        orgId: currentOrganizationId,
        data: {
          riskPoolSolutions: localSolutions
        }
      });
    } catch (error: any) {
      console.error('Failed to save policy config', error);
      // Parse error and show notification
      try {
        const errorData = JSON.parse(error.message);
        errorHelper.enqueueErrors(errorData as ServerErrorResponse);
      } catch (e) {
        // Fallback if error is not JSON
        errorHelper.enqueueErrors({
          message: error.message || 'An unexpected error occurred.',
          errorDetails: null,
        } as ServerErrorResponse);
      }
    }
  };

  return (
    <SolutionCard
      solutionKey={selectedSolution!.key}
      title={selectedSolution!.name}
      view={<View solutions={configData?.riskPoolSolutions || []} isLoading={isConfigLoading} />}
      edit={
        <Edit
          solutions={localSolutions}
          onSolutionsChange={handleSolutionsChange}
          isLoading={isConfigLoading}
        />
      }
      onSave={handleSaveClick}
      isLoading={isUpdateLoading}
    />
  );
};

export default Policy;
